/**
 * @file DrawParticipantsPanel.jsx
 * @module ui/src/components/tenant-admin/sections/DrawParticipantsPanel
 * @description
 *   Per-rep draw ledger opening balance initialization panel.
 *   Rendered inside DrawGuaranteeModifier (PayoutAdjustmentsSection) when the
 *   draw plan is approved and a draw program exists.
 *
 *   Two-step CA-1 / CA-2 flow (owner decision S895):
 *     Step 1 — CA-1 or Manager: set opening balance per rep → POST /initialize
 *     Step 2 — CA-2: approve in the Approvals queue (draw_ledger_init item type)
 *
 *   RBAC surface split (owner decision S895 — no leakage between roles):
 *     CA surface : fetches rep list from EP65 GET /api/v1/plans/{planId}/assigned-positions
 *     Manager surface : fetches rep list from EP45 GET /api/v1/manager/{managerId}/team-summary
 *     Both call: POST /api/v1/draw-ledger/{personUuid}/initialize
 *
 *   Props:
 *     planId       {number|string}  — required for CA rep list fetch (EP65)
 *     planStatus   {string}         — only renders when 'approved'
 *     userRole     {string}         — 'comp_admin' | 'manager'
 *     managerId    {string|null}    — required when userRole='manager' (EP45 path param)
 *     fiscalPeriodId {number|null}  — forwarded into initialization body
 *
 * @version 1.0.0
 * @since S895
 * @changelog
 *   1.0.0  S895  Initial build — T3-5 draw ledger initialization UI
 */

import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// Styles — matches PayoutAdjustmentsSection.jsx S object exactly
// ---------------------------------------------------------------------------
const S = {
  card:     { border: '1px solid rgb(var(--color-border))', borderRadius: 8, background: 'rgb(var(--color-surface))', overflow: 'hidden' },
  header:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderBottom: '1px solid rgb(var(--color-border))' },
  body:     { padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  field:    { display: 'flex', flexDirection: 'column', gap: 4 },
  label:    { fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' },
  input:    { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
  select:   { border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '7px 10px', font: 'inherit', fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' },
  grid2:    { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  muted:    { fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  hint:     { fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 3 },
  error:    { fontSize: 12, color: 'rgb(var(--color-danger, #dc2626))', marginTop: 3 },
  infoBox:  { background: 'rgb(var(--color-info, 59 130 246) / 0.07)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'rgb(var(--color-text-muted))' },
  warnBox:  { background: 'rgb(var(--color-warning, 234 179 8) / 0.08)', border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '8px 12px', fontSize: 12 },
  sectionH: { fontSize: 13, fontWeight: 700, margin: '8px 0 4px', color: 'rgb(var(--color-text))' },
};

// ---------------------------------------------------------------------------
// Draw authorization status badge
// drawAuthorizationStatus: 'none' | 'pending' | 'active' | 'revoked'
// ---------------------------------------------------------------------------
const AUTH_STATUS_LABEL = {
  none:    { label: 'Not initialized',    color: 'rgb(var(--color-text-muted))' },
  pending: { label: 'Pending approval',   color: 'rgb(var(--color-warning, 202 138 4))' },
  active:  { label: 'Active',             color: 'rgb(var(--color-success, 22 163 74))' },
  revoked: { label: 'Revoked',            color: 'rgb(var(--color-danger, 220 38 38))' },
};

function AuthStatusBadge({ status }) {
  const cfg = AUTH_STATUS_LABEL[status] ?? AUTH_STATUS_LABEL.none;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Field — matches PayoutAdjustmentsSection Field component
// ---------------------------------------------------------------------------
function Field({ label, hint, error, children, id }) {
  return (
    <div style={S.field}>
      {label && <label htmlFor={id} style={S.label}>{label}</label>}
      {children}
      {hint && !error && <span style={S.hint}>{hint}</span>}
      {error && <span style={S.error} role="alert">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RepInitRow — per-rep opening balance entry form
// Only shown when drawAuthorizationStatus === 'none' (not yet initialized)
// ---------------------------------------------------------------------------
function RepInitRow({ rep, fiscalPeriodId, onSuccess }) {
  const [open, setOpen]           = useState(false);
  const [amount, setAmount]       = useState('');
  const [currency, setCurrency]   = useState('USD');
  const [method, setMethod]       = useState('in_app');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Only show the form row for reps with no authorization yet
  const canInitialize = rep.drawAuthorizationStatus === 'none';

  function validate() {
    const errs = {};
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed < 0) {
      errs.amount = 'Opening balance must be a number ≥ 0.';
    }
    if (!currency || currency.length !== 3) {
      errs.currency = 'Currency must be a 3-character ISO code (e.g. USD).';
    }
    return errs;
  }

  async function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSubmitError(null);
    setSubmitting(true);

    try {
      const res = await fetch(
        `/api/v1/draw-ledger/${rep.personUuid}/initialize`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            openingBalance:      parseFloat(amount),
            currencyCode:        currency.toUpperCase(),
            authorizationMethod: method,
            ...(fiscalPeriodId != null ? { fiscalPeriodId } : {}),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = data?.detail?.error?.code ?? data?.error ?? 'ERROR';
        const msg  = data?.detail?.error?.message ?? data?.detail ?? `HTTP ${res.status}`;
        // 409 = already exists — surface clearly
        if (res.status === 409) {
          throw new Error(`Already initialized: ${msg}`);
        }
        throw new Error(`[${code}] ${msg}`);
      }

      // Success — notify parent to refresh rep list
      onSuccess(rep.positionId, data.authorizationId);
      setOpen(false);
      setAmount('');
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ ...S.card, marginBottom: 0 }}>
      {/* Rep header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
            {rep.fullName}
          </span>
          <span style={S.muted}>{rep.roleCode}{rep.segmentCode ? ` · ${rep.segmentCode}` : ''}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AuthStatusBadge status={rep.drawAuthorizationStatus} />
          {canInitialize && (
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              style={{
                fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 5,
                border: '1px solid rgb(var(--color-border))',
                background: open ? 'rgb(var(--color-surface-muted))' : 'rgb(var(--color-surface))',
                color: 'rgb(var(--color-primary))', cursor: 'pointer',
              }}
            >
              {open ? 'Cancel' : 'Set opening balance'}
            </button>
          )}
        </div>
      </div>

      {/* Expand: opening balance form */}
      {open && canInitialize && (
        <div style={{ ...S.body, borderTop: '1px solid rgb(var(--color-border))' }}>
          {!rep.personUuid && (
            <div style={S.warnBox}>
              This position has no linked user account (personUuid is missing).
              Contact your administrator to link this position before initializing a draw ledger.
            </div>
          )}

          {rep.personUuid && (
            <>
              <div style={S.grid2}>
                <Field label="Opening balance *" id={`draw-ob-${rep.positionId}`} error={fieldErrors.amount}
                       hint="Balance carried in from a prior system or period. Use 0 for new participants.">
                  <input
                    id={`draw-ob-${rep.positionId}`}
                    type="number" min="0" step="0.01"
                    style={S.input}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="e.g. 0 or 3200.00"
                    disabled={submitting}
                  />
                </Field>
                <Field label="Currency *" id={`draw-cur-${rep.positionId}`} error={fieldErrors.currency}>
                  <input
                    id={`draw-cur-${rep.positionId}`}
                    style={S.input}
                    value={currency}
                    onChange={e => setCurrency(e.target.value.toUpperCase())}
                    placeholder="USD" maxLength={3}
                    disabled={submitting}
                  />
                </Field>
              </div>

              <Field label="Authorization method" id={`draw-meth-${rep.positionId}`}
                     hint="How the draw agreement was signed.">
                <select
                  id={`draw-meth-${rep.positionId}`}
                  style={S.select}
                  value={method}
                  onChange={e => setMethod(e.target.value)}
                  disabled={submitting}
                >
                  <option value="in_app">In-app (digital confirmation)</option>
                  <option value="docusign">DocuSign</option>
                  <option value="wet_signature">Wet signature</option>
                </select>
              </Field>

              {submitError && (
                <div style={S.warnBox} role="alert">
                  {submitError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setSubmitError(null); setFieldErrors({}); }}
                  style={{ fontSize: 12, padding: '6px 14px', borderRadius: 5, border: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface))', cursor: 'pointer' }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 5, border: 'none', background: 'rgb(var(--color-primary))', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
                  disabled={submitting || !rep.personUuid}
                >
                  {submitting ? 'Submitting…' : 'Submit for approval'}
                </button>
              </div>

              <div style={S.infoBox}>
                Submitting creates a pending draw authorization. A second Comp Admin must
                approve it before the opening balance is written to the draw ledger.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DrawParticipantsPanel — main export
// S901: Enterprise-grade implementation with server-side pagination, search,
// status filter, sort. Replaces flat client-side render (unusable at 30k reps).
// ---------------------------------------------------------------------------

const PAGE_SIZE_DEFAULT = 50;

// Status filter tabs — order matches server sort logic
const STATUS_TABS = [
  { key: 'all',     label: 'All' },
  { key: 'none',    label: 'Not initialized' },
  { key: 'pending', label: 'Pending approval' },
  { key: 'active',  label: 'Active' },
  { key: 'revoked', label: 'Revoked' },
];

function DrawParticipantsPanel({ planId, planStatus, userRole, managerId, fiscalPeriodId }) {
  // ── Server-side state ────────────────────────────────────────────────────
  const [reps, setReps]             = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount]   = useState(1);
  const [hasMore, setHasMore]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  // ── Control state ────────────────────────────────────────────────────────
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState(''); // debounced
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort]             = useState('name');
  const [order, setOrder]           = useState('asc');
  const [page, setPage]             = useState(0); // zero-based
  const [refreshKey, setRefreshKey] = useState(0);

  const isApproved = planStatus === 'approved';
  const offset     = page * PAGE_SIZE_DEFAULT;

  // ── Debounce search input (300ms) ────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0); // reset to page 1 on new search
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page when filter/sort changes
  useEffect(() => { setPage(0); }, [statusFilter, sort, order]);

  // ── Fetch ────────────────────────────────────────────────────────────────
  // CA path  : EP65 GET /api/v1/plans/{planId}/assigned-positions (server-side params)
  // Manager path: EP45 GET /api/v1/manager/{managerId}/team-summary (client-side only,
  //   EP45 has no server-side pagination — manager teams are bounded by hierarchy depth)
  useEffect(() => {
    if (!isApproved) return;

    let active = true;
    setLoading(true);
    setError(null);

    let url;
    let isManagerPath = false;

    if (userRole === 'comp_admin') {
      if (!planId) return;
      const params = new URLSearchParams({
        limit:  PAGE_SIZE_DEFAULT,
        offset: offset,
        sort,
        order,
      });
      if (search.trim())       params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      url = `/api/v1/plans/${planId}/assigned-positions?${params.toString()}`;
    } else if (userRole === 'manager') {
      if (!managerId || !fiscalPeriodId) return;
      url = `/api/v1/manager/${managerId}/team-summary?period=${fiscalPeriodId}`;
      isManagerPath = true;
    } else {
      return;
    }

    fetch(url, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (!active) return;
        if (!isManagerPath) {
          // EP65 server-paginated response
          setReps(data.assignedPositions ?? []);
          setTotalCount(data.totalCount ?? 0);
          setPageCount(data.pageCount ?? 1);
          setHasMore(data.hasMore ?? false);
        } else {
          // EP45 manager path — normalize, client-side only (bounded team size)
          const normalized = (data.reps ?? []).map(r => ({
            positionId:              r.positionId,
            personUuid:              r.personUuid ?? null,
            fullName:                r.fullName,
            roleCode:                r.roleCode,
            segmentCode:             r.segmentCode ?? null,
            assignmentStatus:        'active',
            drawAuthorizationStatus: 'none',
          }));
          // Client-side filter/sort for manager path
          let filtered = normalized;
          if (search.trim()) {
            const q = search.trim().toLowerCase();
            filtered = filtered.filter(r => r.fullName?.toLowerCase().includes(q));
          }
          if (statusFilter !== 'all') {
            filtered = filtered.filter(r => r.drawAuthorizationStatus === statusFilter);
          }
          if (sort === 'name') {
            filtered.sort((a, b) => order === 'asc'
              ? (a.fullName ?? '').localeCompare(b.fullName ?? '')
              : (b.fullName ?? '').localeCompare(a.fullName ?? ''));
          }
          const total = filtered.length;
          const slice = filtered.slice(offset, offset + PAGE_SIZE_DEFAULT);
          setReps(slice);
          setTotalCount(total);
          setPageCount(Math.ceil(total / PAGE_SIZE_DEFAULT) || 1);
          setHasMore((offset + PAGE_SIZE_DEFAULT) < total);
        }
      })
      .catch(e => {
        if (active) setError(e.message || 'Unable to load participants.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [isApproved, userRole, planId, managerId, fiscalPeriodId,
      search, statusFilter, sort, order, offset, refreshKey]);

  // ── Rep initialization success handler ───────────────────────────────────
  const handleRepSuccess = useCallback((positionId) => {
    setReps(prev =>
      prev.map(r =>
        r.positionId === positionId
          ? { ...r, drawAuthorizationStatus: 'pending' }
          : r
      )
    );
    setTimeout(() => setRefreshKey(k => k + 1), 1500);
  }, []);

  // ── Sort toggle helper ───────────────────────────────────────────────────
  function handleSort(col) {
    if (sort === col) {
      setOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col);
      setOrder('asc');
    }
  }

  function SortIcon({ col }) {
    if (sort !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{order === 'asc' ? '↑' : '↓'}</span>;
  }

  // ── Guard: not approved ──────────────────────────────────────────────────
  if (!isApproved) {
    return (
      <div style={{ ...S.infoBox, marginTop: 12 }}>
        Draw participant initialization is available once this plan is approved.
        After CA-2 approval, return here to set opening balances for each rep.
      </div>
    );
  }

  const startRow = totalCount === 0 ? 0 : offset + 1;
  const endRow   = Math.min(offset + PAGE_SIZE_DEFAULT, totalCount);

  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={S.sectionH}>Draw Participants</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* S904 — AD-76: deep-link into EXECUTE Adjustments hub with plan filter pre-applied (Section 30.5) */}
          {planId != null && (
            <a
              href={`/admin/execute/adjustments?planId=${planId}`}
              style={{
                fontSize: 11, fontWeight: 600,
                color: 'rgb(var(--color-primary))',
                textDecoration: 'none',
                padding: '3px 8px',
                borderRadius: 5,
                border: '1px solid rgb(var(--color-primary) / 0.3)',
                background: 'rgb(var(--color-primary) / 0.06)',
              }}
              title="Open full adjustments hub filtered to this plan"
            >
              Manage participants →
            </a>
          )}
          <button
            type="button"
            onClick={() => setRefreshKey(k => k + 1)}
            style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
            title="Refresh participant list"
            aria-label="Refresh participant list"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div style={S.infoBox}>
        Set the opening balance for each rep enrolled in this draw program.
        Each submission requires a second Comp Admin to approve before the
        balance is written to the draw ledger.
        {userRole === 'manager' && <> Showing your direct and indirect reports only.</>}
      </div>

      {/* ── Controls: search + status filter tabs + sort ───────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

        {/* Search */}
        <div style={{ position: 'relative', maxWidth: 340 }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'rgb(var(--color-text-muted))', pointerEvents: 'none' }}>
            🔍
          </span>
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by name…"
            style={{ ...S.input, paddingLeft: 28, width: '100%' }}
            aria-label="Search participants by name"
          />
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
                border: '1px solid rgb(var(--color-border))',
                background: statusFilter === tab.key
                  ? 'rgb(var(--color-primary))'
                  : 'rgb(var(--color-surface))',
                color: statusFilter === tab.key
                  ? '#fff'
                  : 'rgb(var(--color-text-muted))',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Row count + sort controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...S.muted }}>
          <span>
            {loading ? 'Loading…' : (
              totalCount === 0
                ? 'No participants match'
                : `${startRow}–${endRow} of ${totalCount.toLocaleString()} participant${totalCount !== 1 ? 's' : ''}`
            )}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => handleSort('name')}
              style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))', padding: '2px 4px' }}
            >
              Name <SortIcon col="name" />
            </button>
            <button
              type="button"
              onClick={() => handleSort('status')}
              style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))', padding: '2px 4px' }}
            >
              Status <SortIcon col="status" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && !loading && (
        <div style={S.warnBox} role="alert">{error}</div>
      )}

      {/* ── Rep list — fixed-height scrollable container ────────────────── */}
      <div
        style={{
          maxHeight: 560,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          // Subtle scroll shadow indicator
          backgroundImage: 'linear-gradient(white 30%, rgba(255,255,255,0)), linear-gradient(rgba(255,255,255,0), white 70%)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% 24px, 100% 24px',
          backgroundAttachment: 'local, local',
        }}
        role="list"
        aria-label="Draw participants"
        aria-busy={loading}
      >
        {loading && reps.length === 0 && (
          <div style={{ ...S.muted, padding: '16px 0', textAlign: 'center' }} aria-live="polite">
            Loading participants…
          </div>
        )}

        {!loading && !error && reps.length === 0 && (
          <div style={{ ...S.muted, padding: '16px 0', textAlign: 'center' }}>
            {search.trim() || statusFilter !== 'all'
              ? 'No participants match the current filter. Try clearing the search or changing the status filter.'
              : 'No active participants found. Assign positions to this plan first.'}
          </div>
        )}

        {reps.map(rep => (
          <div key={rep.positionId} role="listitem">
            <RepInitRow
              rep={rep}
              fiscalPeriodId={fiscalPeriodId}
              onSuccess={handleRepSuccess}
            />
          </div>
        ))}

        {/* Inline loading indicator for page changes (reps already visible) */}
        {loading && reps.length > 0 && (
          <div style={{ ...S.muted, padding: '8px 0', textAlign: 'center' }} aria-live="polite">
            Loading…
          </div>
        )}
      </div>

      {/* ── Pagination controls ─────────────────────────────────────────── */}
      {totalCount > PAGE_SIZE_DEFAULT && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
          <button
            type="button"
            disabled={page === 0 || loading}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 5,
              border: '1px solid rgb(var(--color-border))',
              background: 'rgb(var(--color-surface))',
              cursor: page === 0 || loading ? 'not-allowed' : 'pointer',
              opacity: page === 0 || loading ? 0.4 : 1,
            }}
          >
            ← Previous
          </button>

          <span style={{ ...S.muted, fontSize: 12 }}>
            Page {page + 1} of {pageCount}
          </span>

          <button
            type="button"
            disabled={!hasMore || loading}
            onClick={() => setPage(p => p + 1)}
            style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 5,
              border: '1px solid rgb(var(--color-border))',
              background: 'rgb(var(--color-surface))',
              cursor: !hasMore || loading ? 'not-allowed' : 'pointer',
              opacity: !hasMore || loading ? 0.4 : 1,
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

DrawParticipantsPanel.propTypes = {
  planId:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  planStatus:    PropTypes.string.isRequired,
  userRole:      PropTypes.oneOf(['comp_admin', 'manager']).isRequired,
  managerId:     PropTypes.string,
  fiscalPeriodId: PropTypes.number,
};

DrawParticipantsPanel.defaultProps = {
  planId:        null,
  managerId:     null,
  fiscalPeriodId: null,
};

export default DrawParticipantsPanel;
